import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ContractTabs from '../components/demenageur/ContractTabs';
import SubscriptionPage from '../pages/demenageur/SubscriptionPage';
import CommissionPage from '../pages/demenageur/CommissionPage';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

// Composant de protection pour la route d'abonnement
const ProtectedSubscriptionRoute = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasActiveCommission, setHasActiveCommission] = useState(false);

  useEffect(() => {
    const checkCommission = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        const commissionRes = await api.getActiveCommission(user.id).catch(e => {
          console.error('Erreur lors de la vérification de la commission:', e);
          return { data: null };
        });

        const commission = commissionRes?.data?.data || commissionRes?.data || null;
        setHasActiveCommission(!!commission);
      } catch (error) {
        console.error('Erreur lors de la vérification de la commission:', error);
      } finally {
        setLoading(false);
      }
    };

    checkCommission();
  }, [user?.id]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  // Si l'utilisateur a un contrat de commission actif, rediriger vers la page commission
  if (hasActiveCommission) {
    return <Navigate to="/demenageur/contrat/commission" replace />;
  }

  return children;
};

// Composant de protection pour la route de commission
const ProtectedCommissionRoute = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        console.log('[ProtectedCommissionRoute] Vérification de l\'abonnement pour l\'utilisateur:', user.id);
        
        let subscriptionRes;
        try {
          subscriptionRes = await api.getActiveSubscription(user.id);
          console.log('[ProtectedCommissionRoute] Réponse API (succès):', subscriptionRes);
        } catch (e) {
          // fetchWithAuth lance une exception pour les 404
          // Si le message d'erreur contient "404" ou "Aucun abonnement", c'est qu'il n'y a pas d'abonnement
          const errorMessage = e.message || '';
          const is404 = errorMessage.includes('404') || 
                       errorMessage.includes('Aucun abonnement') || 
                       errorMessage.includes('NO_ACTIVE_SUBSCRIPTION');
          
          if (is404) {
            console.log('[ProtectedCommissionRoute] Aucun abonnement actif détecté (404 ou message)');
            subscriptionRes = { data: null, success: true };
          } else {
            console.error('[ProtectedCommissionRoute] Erreur lors de la vérification de l\'abonnement:', e);
            subscriptionRes = { data: null };
          }
        }

        console.log('[ProtectedCommissionRoute] Réponse brute:', subscriptionRes);
        
        // Essayer différentes structures de réponse
        let subscription = null;
        if (subscriptionRes?.data?.data && subscriptionRes.data.data !== null) {
          subscription = subscriptionRes.data.data;
        } else if (subscriptionRes?.data && subscriptionRes.data !== null && typeof subscriptionRes.data === 'object' && !Array.isArray(subscriptionRes.data)) {
          // Vérifier que data n'est pas null et est un objet (pas un tableau)
          const dataObj = subscriptionRes.data;
          if (dataObj.id || dataObj.user_id || dataObj.demenageur_id || dataObj.subscription_type) {
            subscription = dataObj;
          }
        }
        
        // Vérifier que subscription n'est pas null, undefined, ou un objet vide
        const hasSubscription = !!subscription && 
                                 subscription !== null && 
                                 typeof subscription === 'object' && 
                                 Object.keys(subscription).length > 0;
        
        console.log('[ProtectedCommissionRoute] Résultat:', {
          hasSubscription,
          subscription,
          subscriptionKeys: subscription ? Object.keys(subscription) : [],
          rawResponse: subscriptionRes
        });
        
        setHasActiveSubscription(hasSubscription);
        
        // Si un abonnement est trouvé, rediriger immédiatement
        if (hasSubscription) {
          console.log('[ProtectedCommissionRoute] Abonnement actif détecté - Redirection vers abonnement');
        }
      } catch (error) {
        console.error('[ProtectedCommissionRoute] Erreur lors de la vérification de l\'abonnement:', error);
        setHasActiveSubscription(false);
      } finally {
        setLoading(false);
      }
    };

    checkSubscription();
  }, [user?.id]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  // Si l'utilisateur a un abonnement actif, rediriger vers la page abonnement
  if (hasActiveSubscription) {
    console.log('[ProtectedCommissionRoute] Redirection vers /demenageur/contrat/abonnement');
    return <Navigate to="/demenageur/contrat/abonnement" replace />;
  }

  return children;
};

const ContractRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<ContractTabs />}>
        <Route index element={<Navigate to="abonnement" replace />} />
        <Route 
          path="abonnement" 
          element={
            <ProtectedSubscriptionRoute>
              <SubscriptionPage />
            </ProtectedSubscriptionRoute>
          } 
        />
        <Route 
          path="commission" 
          element={
            <ProtectedCommissionRoute>
              <CommissionPage />
            </ProtectedCommissionRoute>
          } 
        />
      </Route>
    </Routes>
  );
};

export default ContractRoutes;
